import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@cobalt/react-theme-provider';
import CobaltPagination from '@cobalt-marketplace/react-pagination';
import Box from '@cobalt/react-box';
import Divider from '@cobalt/react-divider';
import Flex from '@cobalt/react-flex';
import { Text } from '@cobalt/react-typography';

import { Select } from '../../index';

export type PaginationProps = {
  page: number;
  pageSize: number;
  maxPage?: number;
  maxPagesCount?: number;
  total: number;
  style?: React.CSSProperties;
  previousText?: string;
  nextText?: string;
  jumpToText?: string;
  onPageChange: (page: number, pageSize: number) => void;
};

function Pagination(props: PaginationProps) {
  const [t] = useTranslation();

  const {
    page,
    pageSize,
    maxPage,
    maxPagesCount = 5,
    total,
    style,
    previousText = t('component.pagination.previous', { defaultValue: 'Previous' }),
    nextText = t('component.pagination.next', { defaultValue: 'Next' }),
    jumpToText = t('component.pagination.jump_to', { defaultValue: 'Jump to' }),
    onPageChange,
  } = props;

  const theme = useTheme();

  const totalPages = useMemo(() => {
    const pageTotal = Math.ceil(total / pageSize);
    if (!maxPage) return pageTotal;
    return pageTotal > maxPage ? maxPage : pageTotal;
  }, [maxPage, pageSize, total]);

  const pageOptions = useMemo(() => {
    return Array(totalPages)
      .fill(1)
      .map((_, i) => {
        const v = (i + 1).toString();
        return { value: v, label: v };
      });
  }, [totalPages]);

  const onPageClick = (page: number) => {
    onPageChange(page, pageSize);
  };

  const onNextClick = () => {
    onPageChange(page + 1, pageSize);
  };

  const onPreviousClick = () => {
    onPageChange(page - 1, pageSize);
  };

  return (
    <>
      <Divider />

      <Flex style={style} alignX="space-between" alignY="center" padding="2" backgroundColor={theme.gray100}>
        <CobaltPagination
          currentPage={page}
          totalPages={totalPages}
          maxPagesCount={maxPagesCount}
          previousLabel={previousText}
          nextLabel={nextText}
          onPageClick={onPageClick}
          onNextClick={onNextClick}
          onPreviousClick={onPreviousClick}
        />

        <Flex alignY="center" paddingRight="4">
          <Box paddingRight="2">
            <Text size="small" color={theme.gray600}>
              {jumpToText}:
            </Text>
          </Box>

          <Select
            style={{ alignItems: 'center', backgroundColor: 'transparent', color: theme.gray800 }}
            contentStyle={{ minWidth: '100px', marginLeft: '-50px' }}
            value={page.toString()}
            options={pageOptions}
            borderless
            onChange={(pageNum) => onPageChange(parseInt(pageNum as string), pageSize)}
          />
        </Flex>
      </Flex>
      <Divider />
    </>
  );
}

export default Pagination;
